// ── WhatsAppBroadcastDetailPage ────────────────────────────────────
//
// Shows a single broadcast: metadata, stats, recipient table.
// Actions: Launch, Schedule, Pause, Resume, Cancel, Delete (draft only).

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  BarChart2,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  PauseCircle,
  PlayCircle,
  RefreshCw,
  Trash2,
  XCircle,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { toast } from '@/components/ui/toast';
import { ConfirmModal } from '@/components/ConfirmModal';
import { useSort, SortTh, TableHeadRow } from '@/components/ui/sortable-table';
import {
  getBroadcast,
  getBroadcastStats,
  getBroadcastRecipients,
  launchBroadcast,
  scheduleBroadcast,
  pauseBroadcast,
  resumeBroadcast,
  cancelBroadcast,
  deleteBroadcast,
  previewBroadcastAudience,
  type BroadcastRow,
  type BroadcastStatus,
} from '../api/whatsapp.api';
import { DateTimeInput } from '@/components/ui/DateTimeInput';

// ── Helpers ────────────────────────────────────────────────────────

const STATUS_BADGE: Record<BroadcastStatus, string> = {
  DRAFT: 'bg-slate-100 text-slate-600',
  SCHEDULED: 'bg-blue-50 text-blue-700',
  RUNNING: 'bg-emerald-50 text-emerald-700',
  PAUSED: 'bg-amber-50 text-amber-700',
  COMPLETED: 'bg-indigo-50 text-indigo-700',
  CANCELED: 'bg-slate-100 text-slate-400',
  FAILED: 'bg-red-50 text-red-700',
};

function StatCard({
  label,
  value,
  pct,
  color,
}: {
  label: string;
  value: number;
  pct?: number;
  color: string;
}) {
  return (
    <div className={`rounded-xl border p-4 ${color}`}>
      <p className="text-[11px] font-medium uppercase tracking-wide opacity-70">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value.toLocaleString()}</p>
      {pct !== undefined && (
        <p className="mt-0.5 text-xs opacity-60">{pct}%</p>
      )}
    </div>
  );
}

// ── Schedule modal ─────────────────────────────────────────────────

function ScheduleModal({
  onSchedule,
  onCancel,
}: {
  onSchedule: (iso: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    if (!value) { setError('Pick a date and time.'); return; }
    const d = new Date(value);
    if (d <= new Date()) { setError('Must be in the future.'); return; }
    onSchedule(d.toISOString());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl">
        <h3 className="mb-4 text-base font-semibold text-slate-800">Schedule broadcast</h3>
        <DateTimeInput mode="datetime" value={value} onChange={setValue} />
        {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
        <div className="mt-4 flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 rounded-md border border-gray-200 py-2 text-sm text-slate-600"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            className="flex-1 rounded-md bg-blue-600 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Schedule
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Recipient status badge ─────────────────────────────────────────

const RECIPIENT_BADGE: Record<string, string> = {
  PENDING: 'bg-slate-100 text-slate-500',
  SENT: 'bg-blue-50 text-blue-600',
  DELIVERED: 'bg-emerald-50 text-emerald-700',
  READ: 'bg-indigo-50 text-indigo-700',
  FAILED: 'bg-red-50 text-red-600',
};

// ── Main page ─────────────────────────────────────────────────────

export default function WhatsAppBroadcastDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [broadcast, setBroadcast] = useState<BroadcastRow | null>(null);
  const [stats, setStats] = useState<{
    totalRecipients: number;
    sentCount: number;
    deliveredCount: number;
    readCount: number;
    failedCount: number;
    progressPct: number;
  } | null>(null);
  const [recipients, setRecipients] = useState<any[]>([]);
  const [recipientsPage, setRecipientsPage] = useState(1);
  const [recipientsTotalPages, setRecipientsTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(false);
  const [preview, setPreview] = useState<any | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [showSchedule, setShowSchedule] = useState(false);
  const [confirmAction, setConfirmAction] = useState<
    'launch' | 'cancel' | 'delete' | null
  >(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [b, s] = await Promise.all([getBroadcast(id), getBroadcastStats(id)]);
      setBroadcast(b);
      setStats(s);
    } catch {
      toast('error', 'Failed to load broadcast');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadRecipients = useCallback(async () => {
    if (!id) return;
    try {
      const res = await getBroadcastRecipients(id, { page: recipientsPage, limit: 25 });
      setRecipients(res.data ?? []);
      setRecipientsTotalPages(res.totalPages ?? 1);
    } catch {
      /* ignore */
    }
  }, [id, recipientsPage]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRecipients(); }, [loadRecipients]);

  const { sorted: sortedRecipients, sort, onSort } = useSort(recipients);

  const loadPreview = async () => {
    if (!id) return;
    setPreviewLoading(true);
    try {
      const p = await previewBroadcastAudience(id);
      setPreview(p);
    } catch (e: any) {
      toast('error', e?.response?.data?.message ?? 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const doAction = async (
    action: () => Promise<any>,
    successMsg: string,
  ) => {
    setActioning(true);
    try {
      await action();
      toast('success', successMsg);
      load();
    } catch (e: any) {
      toast('error', e?.response?.data?.message ?? 'Action failed');
    } finally {
      setActioning(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Broadcast">
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (!broadcast) {
    return (
      <DashboardLayout title="Broadcast not found">
        <div className="flex flex-col items-center py-24 text-slate-400">
          <AlertCircle className="mb-3 h-10 w-10" />
          <p>Broadcast not found.</p>
          <button
            onClick={() => navigate('/whatsapp/broadcasts')}
            className="mt-4 text-indigo-600 hover:underline"
          >
            Back to broadcasts
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const s = broadcast.status;
  const isDraft = s === 'DRAFT';
  const isRunning = s === 'RUNNING';
  const isPaused = s === 'PAUSED';
  const isTerminal = s === 'COMPLETED' || s === 'CANCELED' || s === 'FAILED';
  const total = stats?.totalRecipients ?? broadcast.totalRecipients;

  return (
    <DashboardLayout title={broadcast.name}>
      <div className="mx-auto max-w-4xl space-y-6 px-4 py-6">
        {/* Back + header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/whatsapp/broadcasts')}
              className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-semibold text-slate-800">{broadcast.name}</h1>
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    STATUS_BADGE[broadcast.status] ?? 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {broadcast.status}
                </span>
              </div>
              {broadcast.description && (
                <p className="text-sm text-slate-500">{broadcast.description}</p>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={load}
              className="rounded-md border border-gray-200 p-2 text-slate-500 hover:bg-slate-50"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>

            {isDraft && (
              <>
                <button
                  onClick={loadPreview}
                  disabled={previewLoading}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                >
                  {previewLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BarChart2 className="h-3.5 w-3.5" />}
                  Preview audience
                </button>
                <button
                  onClick={() => setShowSchedule(true)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"
                >
                  <Calendar className="h-3.5 w-3.5" />
                  Schedule
                </button>
                <button
                  onClick={() => setConfirmAction('launch')}
                  disabled={actioning}
                  className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:bg-slate-300"
                >
                  {actioning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
                  Launch now
                </button>
                <button
                  onClick={() => setConfirmAction('delete')}
                  className="rounded-md p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}

            {isRunning && (
              <button
                onClick={() => doAction(() => pauseBroadcast(id!), 'Broadcast paused')}
                disabled={actioning}
                className="inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700"
              >
                {actioning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PauseCircle className="h-3.5 w-3.5" />}
                Pause
              </button>
            )}

            {isPaused && (
              <>
                <button
                  onClick={() => doAction(() => resumeBroadcast(id!), 'Broadcast resumed')}
                  disabled={actioning}
                  className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white"
                >
                  {actioning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
                  Resume
                </button>
                <button
                  onClick={() => setConfirmAction('cancel')}
                  className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600"
                >
                  <XCircle className="h-3.5 w-3.5" />
                  Cancel
                </button>
              </>
            )}

            {(isRunning || s === 'SCHEDULED') && !isTerminal && (
              <button
                onClick={() => setConfirmAction('cancel')}
                className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 hover:bg-red-100"
              >
                <XCircle className="h-3.5 w-3.5" />
                Cancel
              </button>
            )}
          </div>
        </div>

        {/* Audience preview panel */}
        {preview && (
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-blue-800">Audience Preview</p>
              <button onClick={() => setPreview(null)} className="text-blue-400 hover:text-blue-700">
                ✕
              </button>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-lg bg-white p-3 shadow-sm">
                <p className="text-[11px] text-slate-400">Eligible</p>
                <p className="text-xl font-bold text-emerald-600">
                  {preview.eligibleCount?.toLocaleString() ?? '—'}
                </p>
              </div>
              <div className="rounded-lg bg-white p-3 shadow-sm">
                <p className="text-[11px] text-slate-400">Excluded</p>
                <p className="text-xl font-bold text-slate-600">
                  {preview.excludedCount?.toLocaleString() ?? '—'}
                </p>
              </div>
              {preview.estimatedCostUsd != null && (
                <div className="rounded-lg bg-white p-3 shadow-sm">
                  <p className="text-[11px] text-slate-400">Est. cost (USD)</p>
                  <p className="text-xl font-bold text-slate-700">
                    ${preview.estimatedCostUsd.toFixed(2)}
                  </p>
                </div>
              )}
              {preview.sampleRecipient && (
                <div className="rounded-lg bg-white p-3 shadow-sm">
                  <p className="text-[11px] text-slate-400">Sample recipient</p>
                  <p className="text-sm font-medium text-slate-700">
                    {preview.sampleRecipient.name ?? preview.sampleRecipient.waId}
                  </p>
                </div>
              )}
            </div>
            {preview.excludedBreakdown && Object.keys(preview.excludedBreakdown).length > 0 && (
              <div className="mt-2 text-xs text-blue-700">
                Exclusions:{' '}
                {Object.entries(preview.excludedBreakdown)
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(' · ')}
              </div>
            )}
          </div>
        )}

        {/* Stats cards */}
        {total > 0 && stats && (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatCard
                label="Sent"
                value={stats.sentCount}
                pct={total ? Math.round((stats.sentCount / total) * 100) : 0}
                color="border-blue-100 bg-blue-50 text-blue-800"
              />
              <StatCard
                label="Delivered"
                value={stats.deliveredCount}
                pct={total ? Math.round((stats.deliveredCount / total) * 100) : 0}
                color="border-emerald-100 bg-emerald-50 text-emerald-800"
              />
              <StatCard
                label="Read"
                value={stats.readCount}
                pct={total ? Math.round((stats.readCount / total) * 100) : 0}
                color="border-indigo-100 bg-indigo-50 text-indigo-800"
              />
              <StatCard
                label="Failed"
                value={stats.failedCount}
                pct={total ? Math.round((stats.failedCount / total) * 100) : 0}
                color="border-red-100 bg-red-50 text-red-800"
              />
            </div>

            {/* Progress bar */}
            <div>
              <div className="mb-1 flex justify-between text-xs text-slate-500">
                <span>Progress</span>
                <span>{stats.progressPct}% — {total.toLocaleString()} recipients</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                <div
                  className="h-full rounded-full bg-indigo-500 transition-all"
                  style={{ width: `${stats.progressPct}%` }}
                />
              </div>
            </div>
          </>
        )}

        {/* Recipients table */}
        {(recipients.length > 0 || !isDraft) && (
          <div>
            <h2 className="mb-3 text-sm font-semibold text-slate-700">Recipients</h2>
            {recipients.length === 0 ? (
              <p className="text-sm text-slate-400">No recipient data yet.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="min-w-full text-sm">
                  <thead>
                    <TableHeadRow>
                      <SortTh label="Contact" sortKey="contactName" sort={sort} onSort={onSort} />
                      <SortTh label="WA ID" sortKey="contactWaId" sort={sort} onSort={onSort} />
                      <SortTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
                      <SortTh label="Updated" sortKey="updatedAt" sort={sort} onSort={onSort} />
                    </TableHeadRow>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {sortedRecipients.map((r: any) => (
                      <tr key={r.id} className="hover:bg-slate-50">
                        <td className="px-4 py-2.5 font-medium text-slate-800">
                          {r.contactName ?? r.contactWaId}
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-slate-500">
                          {r.contactWaId}
                        </td>
                        <td className="px-4 py-2.5">
                          <span
                            className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                              RECIPIENT_BADGE[r.status] ?? 'bg-slate-100 text-slate-500'
                            }`}
                          >
                            {r.status}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-slate-400">
                          {new Date(r.updatedAt).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {recipientsTotalPages > 1 && (
                  <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-4 py-2">
                    <button
                      onClick={() => setRecipientsPage((p) => Math.max(1, p - 1))}
                      disabled={recipientsPage <= 1}
                      className="rounded p-1 hover:bg-slate-100 disabled:opacity-40"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="text-xs text-slate-500">
                      {recipientsPage}/{recipientsTotalPages}
                    </span>
                    <button
                      onClick={() => setRecipientsPage((p) => Math.min(recipientsTotalPages, p + 1))}
                      disabled={recipientsPage >= recipientsTotalPages}
                      className="rounded p-1 hover:bg-slate-100 disabled:opacity-40"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Metadata */}
        <div className="rounded-xl border border-gray-100 bg-slate-50 p-4 text-xs text-slate-500">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <div>
              <p className="font-medium text-slate-600">Created</p>
              <p>{new Date(broadcast.createdAt).toLocaleString()}</p>
            </div>
            {broadcast.scheduledFor && (
              <div>
                <p className="font-medium text-slate-600">Scheduled for</p>
                <p>{new Date(broadcast.scheduledFor).toLocaleString()}</p>
              </div>
            )}
            {broadcast.startedAt && (
              <div>
                <p className="font-medium text-slate-600">Started</p>
                <p>{new Date(broadcast.startedAt).toLocaleString()}</p>
              </div>
            )}
            {broadcast.completedAt && (
              <div>
                <p className="font-medium text-slate-600">Completed</p>
                <p>{new Date(broadcast.completedAt).toLocaleString()}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Schedule modal */}
      {showSchedule && (
        <ScheduleModal
          onSchedule={async (iso) => {
            setShowSchedule(false);
            await doAction(() => scheduleBroadcast(id!, iso), 'Broadcast scheduled');
          }}
          onCancel={() => setShowSchedule(false)}
        />
      )}

      {/* Confirm: launch */}
      {confirmAction === 'launch' && (
        <ConfirmModal
          title="Launch broadcast now"
          message={`Start sending "${broadcast.name}" immediately? This will enqueue messages to all eligible recipients and cannot be undone (only paused or cancelled mid-flight).`}
          confirmText="Launch" isOpen={true}
          onConfirm={async () => {
            setConfirmAction(null);
            await doAction(() => launchBroadcast(id!), 'Broadcast launched!');
          }}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {/* Confirm: cancel */}
      {confirmAction === 'cancel' && (
        <ConfirmModal
          title="Cancel broadcast"
          message="Cancel this broadcast? No further messages will be sent. This cannot be reversed."
          confirmText="Cancel broadcast" isOpen={true}
          onConfirm={async () => {
            setConfirmAction(null);
            await doAction(() => cancelBroadcast(id!), 'Broadcast cancelled');
          }}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {/* Confirm: delete draft */}
      {confirmAction === 'delete' && (
        <ConfirmModal
          title="Delete draft"
          message={`Permanently delete the draft "${broadcast.name}"?`}
          confirmText="Delete" isOpen={true}
          onConfirm={async () => {
            setConfirmAction(null);
            try {
              await deleteBroadcast(id!);
              toast('success', 'Draft deleted');
              navigate('/whatsapp/broadcasts');
            } catch {
              toast('error', 'Failed to delete draft');
            }
          }}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </DashboardLayout>
  );
}
